import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ChevronRight, Copy, Cpu, ExternalLink, HardDrive, MemoryStick, Play, RotateCw, Send, Square, Terminal, Trash2, Users } from 'lucide-react';
import type { MinecraftLimits, MinecraftServer, MinecraftSoftware, MinecraftState, MinecraftVersionInfo } from '@spawnwire/shared';
import { Button, clsx, Field, IconButton, Input, Select, Spinner } from '../../components/ui';
import { errorMessage, http } from '../../lib/api';
import { getSocket, onReady } from '../../lib/gateway';
import { copyText, useNow } from '../../lib/hooks';
import { ui } from '../../store/ui';
import { FileManager } from './FileManager';
import { minecraft, useMinecraft } from './store';

const SOFTWARE: { value: MinecraftSoftware; label: string; hint: string }[] = [
  { value: 'vanilla', label: 'Vanilla', hint: 'The official Minecraft server, exactly as Mojang ships it.' },
  { value: 'paper', label: 'Paper', hint: 'Faster than Vanilla and supports Bukkit and Paper plugins.' },
];

export const STATE_LABEL: Record<MinecraftState, string> = {
  installing: 'Setting up',
  install_failed: 'Setup failed',
  offline: 'Offline',
  starting: 'Starting',
  running: 'Online',
  stopping: 'Stopping',
  suspended: 'Suspended',
  unknown: 'Unavailable',
};

export const STATE_TONE: Record<MinecraftState, string> = {
  installing: 'bg-warning',
  install_failed: 'bg-danger',
  offline: 'bg-offline',
  starting: 'bg-warning',
  running: 'bg-success',
  stopping: 'bg-warning',
  suspended: 'bg-danger',
  unknown: 'bg-offline',
};

const gb = (bytes: number) => `${(bytes / 1024 ** 3).toFixed(bytes >= 1024 ** 3 ? 1 : 2)} GB`;

export function CreateMinecraftServer({ idleSeconds, onCreated }: { idleSeconds: number; onCreated: (server: MinecraftServer) => void }) {
  const [name, setName] = useState('');
  const [software, setSoftware] = useState<MinecraftSoftware>('paper');
  const [mode, setMode] = useState<'latest' | 'specific'>('latest');
  const [version, setVersion] = useState('');
  const [latest, setLatest] = useState<string | null>(null);
  const [eula, setEula] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setLatest(null);
    http
      .get<MinecraftVersionInfo>(`/minecraft/versions?software=${software}`)
      .then((v) => live && setLatest(v.latest))
      .catch(() => live && setLatest(null));
    return () => {
      live = false;
    };
  }, [software]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const server = await http.post<MinecraftServer>('/minecraft/servers', {
        name: name.trim(),
        software,
        version: mode === 'specific' ? version.trim() : null,
        acceptEula: eula,
      });
      minecraft.upsert(server);
      ui.toast('Your server is being set up. This takes a minute or two.', 'success');
      onCreated(server);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const minutes = Math.round(idleSeconds / 60);
  return (
    <form onSubmit={submit} className="mx-auto w-full max-w-xl space-y-5 rounded-xl border border-line bg-sidebar p-6">
      <div>
        <h2 className="text-lg font-bold text-fg">Create your Minecraft server</h2>
        <p className="mt-1 text-sm text-muted">
          Free, with 2 GB of RAM and 5 GB of storage. It turns itself off after {minutes >= 1 ? `${minutes} minute${minutes === 1 ? '' : 's'}` : `${idleSeconds} seconds`} with nobody online, and you can start it again any time.
        </p>
      </div>
      <Field label="Server name" htmlFor="mc-name">
        <Input id="mc-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={32} placeholder="My Survival World" required />
      </Field>
      <Field label="Software" hint={SOFTWARE.find((s) => s.value === software)?.hint}>
        <div role="radiogroup" aria-label="Software" className="grid grid-cols-2 gap-2">
          {SOFTWARE.map((s) => (
            <button
              key={s.value}
              type="button"
              role="radio"
              aria-checked={software === s.value}
              onClick={() => setSoftware(s.value)}
              className={clsx('rounded-lg border px-3 py-2.5 text-sm font-semibold transition-colors', software === s.value ? 'border-accent bg-accent-soft text-fg' : 'border-line bg-main text-muted hover:text-fg')}
            >
              {s.label}
            </button>
          ))}
        </div>
      </Field>
      <Field label="Minecraft version" htmlFor="mc-version-mode" hint={mode === 'specific' ? `We check that ${SOFTWARE.find((s) => s.value === software)?.label} supports the version you enter.` : undefined}>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Select
            id="mc-version-mode"
            value={mode}
            onChange={setMode}
            className="sm:w-56"
            options={[
              { value: 'latest', label: latest ? `Latest (${latest})` : 'Latest' },
              { value: 'specific', label: 'Choose a version' },
            ]}
          />
          {mode === 'specific' && <Input value={version} onChange={(e) => setVersion(e.target.value)} placeholder="e.g. 1.21.4" aria-label="Minecraft version" maxLength={20} required />}
        </div>
      </Field>
      <label className="flex items-start gap-3 text-sm text-muted">
        <input type="checkbox" checked={eula} onChange={(e) => setEula(e.target.checked)} className="mt-0.5 size-4 accent-[var(--p-accent)]" />
        <span>
          I accept the{' '}
          <a href="https://aka.ms/MinecraftEULA" target="_blank" rel="noopener noreferrer" className="text-link hover:underline">
            Minecraft EULA
          </a>
          .
        </span>
      </label>
      {error && <p className="text-sm text-danger">{error}</p>}
      <Button type="submit" className="w-full" loading={busy} disabled={!name.trim() || !eula || (mode === 'specific' && !version.trim())}>
        Create server
      </Button>
    </form>
  );
}

function Meter({ icon: Icon, label, value, max, text }: { icon: typeof Cpu; label: string; value: number; max: number; text: string }) {
  const pct = Math.max(0, Math.min(100, max > 0 ? (value / max) * 100 : 0));
  return (
    <div className="rounded-lg bg-main p-3">
      <div className="flex items-center justify-between text-xs text-muted">
        <span className="flex items-center gap-1.5 font-semibold uppercase tracking-wide">
          <Icon className="size-3.5" aria-hidden /> {label}
        </span>
        <span>{text}</span>
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-active" role="meter" aria-label={label} aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
        <div className={clsx('h-full rounded-full transition-[width]', pct > 90 ? 'bg-danger' : pct > 70 ? 'bg-warning' : 'bg-accent')} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function Console({ server }: { server: MinecraftServer }) {
  const lines = useMinecraft((s) => s.console[server.id]);
  const [command, setCommand] = useState('');
  const [error, setError] = useState<string | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  const ready = server.state !== 'installing' && server.state !== 'install_failed';

  useEffect(() => {
    if (!ready) return;
    const subscribe = () => {
      getSocket()?.emit('mc:console:subscribe', { serverId: server.id }, (res) => {
        if (res.ok) {
          minecraft.setConsole(server.id, res.data?.lines ?? []);
          setError(null);
        } else setError(res.error);
      });
    };
    subscribe();
    const off = onReady(subscribe);
    return () => {
      off();
      getSocket()?.emit('mc:console:unsubscribe', { serverId: server.id });
    };
  }, [server.id, ready]);

  useEffect(() => {
    if (box.current && stick.current) box.current.scrollTop = box.current.scrollHeight;
  }, [lines]);

  async function send(e: FormEvent) {
    e.preventDefault();
    const text = command.trim().replace(/^\//, '');
    if (!text) return;
    try {
      await http.post(`/minecraft/servers/${server.id}/command`, { command: text });
      setCommand('');
    } catch (err) {
      ui.toast(errorMessage(err), 'error');
    }
  }

  return (
    // A fixed height (filling the page on wide screens): new output scrolls inside the box instead of growing it.
    <section aria-label="Console" className="flex h-[60vh] min-h-[320px] min-w-0 shrink-0 flex-col overflow-hidden rounded-xl border border-line bg-overlay lg:h-[calc(100dvh-11.5rem)] lg:flex-1">
      <div className="flex items-center gap-2 border-b border-line px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted">
        <Terminal className="size-4" aria-hidden /> Console
      </div>
      <div
        ref={box}
        onScroll={(e) => {
          const el = e.currentTarget;
          stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
        }}
        className="scroll-thin min-h-0 flex-1 overflow-y-auto px-4 py-3 font-mono text-xs leading-5 text-fg [overflow-wrap:anywhere]"
        role="log"
        aria-live="off"
      >
        {!ready ? (
          <p className="text-muted">The console opens once your server has finished setting up.</p>
        ) : error ? (
          <p className="text-danger">{error}</p>
        ) : !lines ? (
          <Spinner className="size-4" label="Connecting to console" />
        ) : lines.length === 0 ? (
          <p className="text-muted">Nothing here yet. Start the server to see its output.</p>
        ) : (
          lines.map((line, i) => (
            <div key={i} className="whitespace-pre-wrap">
              {line}
            </div>
          ))
        )}
      </div>
      <form onSubmit={send} className="flex items-center gap-2 border-t border-line p-2">
        <span className="pl-2 font-mono text-sm text-faint" aria-hidden>
          &gt;
        </span>
        <input
          value={command}
          onChange={(e) => setCommand(e.target.value)}
          placeholder={server.state === 'running' ? 'Type a command, like say Hello or op YourName' : 'Start the server to send commands'}
          disabled={server.state !== 'running'}
          aria-label="Console command"
          maxLength={256}
          className="min-w-0 flex-1 bg-transparent font-mono text-sm text-fg outline-none placeholder:text-faint disabled:opacity-60"
        />
        <IconButton icon={Send} label="Send command" type="submit" disabled={server.state !== 'running' || !command.trim()} />
      </form>
    </section>
  );
}

/** Whether a state should pulse its status dot (something is in progress). */
export const isTransitional = (state: MinecraftState) => state === 'starting' || state === 'stopping' || state === 'installing';

export function MinecraftServerCard({ server, limits, onDeleted }: { server: MinecraftServer; limits: MinecraftLimits; onDeleted: () => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const now = useNow(1000);
  const canStart = server.state === 'offline';
  const canStop = server.state === 'running' || server.state === 'starting';

  async function power(action: 'start' | 'stop' | 'restart') {
    setBusy(action);
    try {
      minecraft.upsert(await http.post<MinecraftServer>(`/minecraft/servers/${server.id}/power`, { action }));
    } catch (err) {
      ui.toast(errorMessage(err), 'error');
    } finally {
      setBusy(null);
    }
  }

  function remove() {
    ui.confirm({
      title: `Delete ${server.name}?`,
      body: 'This permanently deletes the server and its world. It cannot be undone.',
      confirmLabel: 'Delete server',
      danger: true,
      onConfirm: async () => {
        await http.del(`/minecraft/servers/${server.id}`);
        minecraft.remove(server.id);
        onDeleted();
      },
    });
  }

  const idleLeft = server.idleStopAt ? Math.max(0, Math.ceil((Date.parse(server.idleStopAt) - now) / 1000)) : null;
  const r = server.resources;
  return (
    // Console on the left, everything about the server on the right (stacked on phones with the info first),
    // and the file manager underneath.
    <div className="flex flex-col gap-4">
      <div className="flex min-h-0 flex-1 flex-col gap-4 lg:flex-row">
        <aside className="order-first flex shrink-0 flex-col gap-4 lg:order-last lg:w-80">
          <section className="rounded-xl border border-line bg-sidebar p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="truncate text-xl font-bold text-fg">{server.name}</h2>
                <p className="mt-0.5 text-sm text-muted">
                  {server.software === 'paper' ? 'Paper' : 'Vanilla'} {server.version}
                </p>
              </div>
              <span className="flex shrink-0 items-center gap-2 rounded-full bg-main px-3 py-1 text-sm font-medium text-fg">
                <span className={clsx('size-2.5 rounded-full', STATE_TONE[server.state], isTransitional(server.state) && 'animate-pulse')} aria-hidden />
                {STATE_LABEL[server.state]}
              </span>
            </div>

            <button
              type="button"
              onClick={() => void copyText(server.address).then(() => ui.toast('Server address copied.', 'success'))}
              className="mt-4 flex w-full items-center justify-between gap-2 rounded-lg bg-main px-3 py-2 font-mono text-sm text-fg hover:bg-hover"
              aria-label={`Copy server address ${server.address}`}
            >
              <span className="truncate">{server.address}</span> <Copy className="size-4 shrink-0 text-muted" aria-hidden />
            </button>
            {server.players && (
              <p className="mt-2 flex items-center gap-1.5 text-sm text-fg">
                <Users className="size-4 text-muted" aria-hidden /> {server.players.online} / {server.players.max} players online
              </p>
            )}

            {server.state === 'installing' && <p className="mt-3 text-sm text-muted">Setting up your server. This usually takes a minute or two; this page updates when it's ready.</p>}
            {server.state === 'install_failed' && <p className="mt-3 text-sm text-danger">Setting up this server failed. Delete it and create it again, or pick another version.</p>}
            {server.state === 'suspended' && (
              <p className="mt-3 rounded-lg bg-danger/10 p-3 text-sm text-fg">
                Spawnwire staff suspended this server{server.suspendReason ? `: ${server.suspendReason}` : '.'} It can't be started, and its files are locked, until they lift the suspension.
              </p>
            )}
            {idleLeft !== null && (
              <p className="mt-3 text-sm text-warning">
                Nobody is online. The server turns off in {Math.floor(idleLeft / 60)}:{String(idleLeft % 60).padStart(2, '0')} unless someone joins.
              </p>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
              {canStart && (
                <Button icon={Play} className="flex-1" loading={busy === 'start'} onClick={() => void power('start')}>
                  Start
                </Button>
              )}
              {canStop && (
                <Button icon={Square} variant="secondary" className="flex-1" loading={busy === 'stop'} onClick={() => void power('stop')}>
                  Stop
                </Button>
              )}
              {server.state === 'running' && (
                <Button icon={RotateCw} variant="secondary" className="flex-1" loading={busy === 'restart'} onClick={() => void power('restart')}>
                  Restart
                </Button>
              )}
            </div>
          </section>

          <section className="space-y-2 rounded-xl border border-line bg-sidebar p-4" aria-label="Usage">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-faint">Usage</h3>
            {r ? (
              <>
                <Meter icon={MemoryStick} label="Memory" value={r.memoryBytes} max={limits.memoryMb * 1024 ** 2} text={`${gb(r.memoryBytes)} / ${gb(limits.memoryMb * 1024 ** 2)}`} />
                <Meter icon={Cpu} label="CPU" value={r.cpuPercent} max={limits.cpuPercent} text={`${Math.round(r.cpuPercent)}% / ${limits.cpuPercent}%`} />
                <Meter icon={HardDrive} label="Storage" value={r.diskBytes} max={limits.diskMb * 1024 ** 2} text={`${gb(r.diskBytes)} / ${gb(limits.diskMb * 1024 ** 2)}`} />
              </>
            ) : (
              <p className="text-sm text-muted">
                Shows while the server is running. Limits: {gb(limits.memoryMb * 1024 ** 2)} RAM, {limits.cpuPercent}% CPU, {gb(limits.diskMb * 1024 ** 2)} storage.
              </p>
            )}
          </section>

          <section className="rounded-xl border border-line bg-sidebar p-4 text-sm text-muted" aria-label="Game settings">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-faint">Game settings</h3>
            <dl className="grid grid-cols-[1fr_auto] gap-y-1">
              <dt>Render distance</dt>
              <dd className="text-fg">6 chunks</dd>
              <dt>Simulation distance</dt>
              <dd className="text-fg">4 chunks</dd>
              <dt>Max players</dt>
              <dd className="text-fg">16</dd>
            </dl>
          </section>

          <a
            href="https://craftnodes.com"
            target="_blank"
            rel="noopener noreferrer"
            className="group block rounded-xl border border-line bg-sidebar p-4 text-sm transition-colors hover:border-accent/60 hover:bg-elevated"
          >
            <span className="mb-2 flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-faint">
              Sponsored <ExternalLink className="size-3.5 opacity-0 transition-opacity group-hover:opacity-100" aria-hidden />
            </span>
            <span className="block font-semibold text-fg">Cheap 24/7 Minecraft hosting with CraftNodes</span>
            <span className="mt-1 block text-muted">Keep your world online around the clock, with no auto-shutdown. Plans start from</span>
            <span className="mt-2 flex items-baseline gap-1">
              <span className="text-2xl font-bold text-accent">$1.50</span>
              <span className="text-muted">/ month</span>
            </span>
            <span className="mt-3 inline-flex items-center gap-1 font-medium text-accent group-hover:underline">
              View plans at craftnodes.com <ChevronRight className="size-4" aria-hidden />
            </span>
          </a>

          <Button icon={Trash2} variant="ghost" className="text-danger hover:text-danger" onClick={remove}>
            Delete server
          </Button>
        </aside>
        <Console server={server} />
      </div>
      <FileManager server={server} uploadMaxBytes={limits.uploadMaxBytes} />
    </div>
  );
}
